import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@se/se.platform.base';
import { EWebViewerType, ImagingOpenParams, PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { isEmpty, merge } from 'lodash-es';
import { firstValueFrom, fromEvent, Subscription } from 'rxjs';
import {
    convertPacsTupleDatesFromISO,
    fromNg1OpenParams,
    fromNg1PacsStudyData,
    keyImageFromMessage,
    PacsStudyDataVnext,
    XmlRequestType,
} from '../common';
import { isViewerBusy, isViewerOpen, isViewerReady, ViewerStatus } from './models';
import {
    seViewerConnected,
    seViewerKeyImageAdd,
    seViewerKeyImageRefresh,
    seViewerKeyImagesDelete,
    seViewerKeyImagesSet,
    seViewerSRAdd,
    seViewerSRDelete,
    seViewerUSKineticMeasureImportRequest,
    seViewerUSMeasureDeleteRequest,
    seViewerUSMeasureDeleteResponse,
    seViewerUSMeasureGetReportResponse,
    seViewerUSMeasureImportJsonRequest,
    seViewerUSMeasureImportRequestThirdParts,
    seViewerUSMeasureImportXmlRequest,
    seViewerUSStressEchoInit,
    seViewerUSStressEchoSelect,
} from '../../../root-store/root.actions';
import { ChildWindow, ChildWindowBuilderService, PopupsBlockedError, ScreenType } from '../../embedding/child.service';
import { BtoaEncoder } from './btoa-encoder';
import { RadioSettingsService } from '../../settings/radio-settings.service';
import { postMessageToWebViewer, ZefiroIncomingMessage } from './zefiro-api';
import {
    DEFAULT_OPEN_IMAGES_OPTIONS,
    ExaminationStatusForImaging,
    ModelKeyImage,
    ModelKeyImageInstanceInfo,
    ModelStructuredReport,
    ModelUSMeasure,
    ModelUSMeasureShow,
    ModelUSStressEchoAssociateImages,
    ModelUSStressEchoInit,
    ModelUSStressEchoSelect,
    OpenImagesOptions,
    RequestModelViewStudies,
} from '../ebit-viewer-api';
import { IViewerHelper } from '../angularjs';
import { ServerRootService } from '../../../utils/routing/serverroot.service';
import { convertPropertiesToCamelCase } from '../../../utils/interop';
import { UserConfigurationSettingsService } from '../../settings/user-configuration-settings.service';
import { DeepPartial } from '../../../utils/types';

export type ViewImagesModel = Omit<RequestModelViewStudies, 'studies'> & { studies: Record<string, string>[] };

function viewImagesModelFromPacsStudyData(studyData: PacsStudyDataVnext[], options: DeepPartial<OpenImagesOptions>) {
    return {
        ...merge({}, DEFAULT_OPEN_IMAGES_OPTIONS, options),
        studies: studyData.map((datum) => ({
            patientID: datum.patient.patientId,
            patientName: datum.patient.fullName,
            patientSex: datum.patient.sex,
            patientBirthDate: datum.patient.birthDate,
            studyDate: datum.study.studyDate,
            accessionNumber: datum.study.accessionNumber,
            studyInstanceUID: datum.study.studyInstanceUID,
            modality: datum.study.modality,
        })),
    };
}

@Injectable({
    providedIn: 'root',
})
export class ZefiroViewerHelper extends ServerRootService implements IViewerHelper {
    readonly isVNext = true;
    readonly viewerType = EWebViewerType.ZEfiRO;

    private readonly baseUrl =
        this.settings.radioSettings?.viewerSettings.viewerSeWebSettings?.baseUrl ?? 'https://suse15-dev';
    private readonly keysUrl =
        this.settings.radioSettings?.viewerSettings.viewerSeWebSettings?.keysUrl ??
        'https://suse15-dev/auth/api/v1/keys';

    private viewer: ViewerStatus = { state: 'closed' };
    private pingInterval?: any;
    private cryptoKey: CryptoKey;

    constructor(
        private actions$: Actions,
        private childWindowBuilder: ChildWindowBuilderService,
        private http: HttpClient,
        private logger: Logger,
        private settings: RadioSettingsService,
        private store: Store,
        private translate: TranslateService,
        private userConfiguration: UserConfigurationSettingsService,
        private zone: NgZone,
    ) {
        super();
    }

    /**
     * Required by the interface.
     */
    async init() {
        // empty
    }

    /**
     * Apertura Viewer:
     * - Non fare nulla se il viewer è già aperto e l'utente è già loggato.
     * - Nel caso il viewer sia aperto, ma non l'utente è già loggato, fai solo login.
     * - Altrimenti, apri il viewer, aspetta che sia disponibie per il login, e poi effettua il login.
     */
    async openViewer(studyData?: PacsStudyDataVnext[]) {
        if (isViewerBusy(this.viewer) || isViewerReady(this.viewer)) {
            return;
        }

        if (!isViewerOpen(this.viewer)) {
            await this.openWindow(studyData);
        }

        // Support updating pairing token?
    }

    async closeViewer() {
        this.logger.debug('Closing viewer zefiro helper');
        if (isViewerOpen(this.viewer)) {
            this.viewer.window.close();
        }
        this.setViewerStateDisconnected();
    }

    async isViewerOpen() {
        return Promise.resolve(isViewerOpen(this.viewer));
    }

    async areImagesOpen(): Promise<boolean> {
        return true;
    }

    private async openWindow(studyData?: PacsStudyDataVnext[]) {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }
        this.viewer = { state: 'opening' };

        try {
            const url = await this.buildViewerUrl(studyData);
            const viewerWindow = await this.childWindowBuilder
                .withUrl(url)
                .onScreen(ScreenType.Extended)
                .withMessageTransformer(new BtoaEncoder())
                .open();

            if (viewerWindow) {
                const messagesSubscription = fromEvent(window, 'message').subscribe((msg: MessageEvent) =>
                    this.zone.run(this.onViewerMessage, this, [convertPropertiesToCamelCase(msg.data)]),
                );
                this.setViewerStateConnected(viewerWindow, messagesSubscription);
                this.listenForActions();
            }
        } catch (err) {
            if (err instanceof PopupsBlockedError) {
                window.alert(this.translate.instant('PopupsBlockedError'));
            }
            this.setViewerStateDisconnected();
        }
    }

    private setViewerStateConnected(window: ChildWindow, messagesSubscription: Subscription) {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }
        this.viewer = { messagesSubscription, state: 'open', window };
    }

    private setViewerStateDisconnected() {
        if ('messagesSubscription' in this.viewer) {
            this.viewer.messagesSubscription.unsubscribe();
        }

        this.viewer = { state: 'closed' };
        this.store.dispatch(seViewerConnected({ isConnected: false }));
    }

    async discardImages(ask = false) {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, { command: 'DISCARD', payload: { ask } });
    }

    async openImages(studyData: PacsStudyDataVnext[], options: DeepPartial<OpenImagesOptions>) {
        if (!isViewerOpen(this.viewer)) {
            await this.openViewer(studyData);
        }
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        // Questo non usa l'API di ViewerApi, ma invia un messaggio diretto al viewer.
        return this.viewer.window.send(
            {
                command: 'VIEW_IMAGES',
                payload: await this.encryptPayload(
                    JSON.stringify(viewImagesModelFromPacsStudyData(studyData, options)),
                ),
            },
            { skipTransform: true },
        );
    }

    async setExaminationStatus(
        _idExaminations: string[],
        examinationStatus: ExaminationStatusForImaging,
        studyInstanceUIDs: string[],
    ) {
        if (!studyInstanceUIDs.length) return;

        if (!isViewerOpen(this.viewer)) {
            return;
        }

        try {
            return postMessageToWebViewer(this.viewer.window, {
                command: 'EXAMINATION_STATUS',
                payload: {
                    idExaminations: studyInstanceUIDs,
                    examinationStatus,
                },
            });
        } catch (err) {
            console.error('Error finding studies:', err);
        }
    }

    async deleteKeyImage?(data: ModelKeyImage): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, {
            command: 'DELETE_KEY_IMAGE',
            payload: data,
        });
    }

    async usStressEchoSelectStage?(stage: string): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, {
            command: 'US_STRESS_ECHO_SELECT_STAGE',
            payload: stage,
        });
    }

    async usStressEchoSelectView?(view: string): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, {
            command: 'US_STRESS_ECHO_SELECT_VIEW',
            payload: view,
        });
    }

    async uSStressEchoAssociateImages?(model: ModelUSStressEchoAssociateImages): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, {
            command: 'US_STRESS_ECHO_ASSOCIATE_IMAGES',
            payload: model,
        });
    }

    openImagesRequired(imagingData: PacsStudyDataNg1[], options?: DeepPartial<ImagingOpenParams>) {
        this.openImages(imagingData.map(fromNg1PacsStudyData), fromNg1OpenParams(options ?? {}));
    }

    discardImagesRequired() {
        this.discardImages();
    }

    closeViewerRequired() {
        this.closeViewer();
    }

    isFormSubmitting() {
        return false;
    }

    private onViewerMessage(msg: ZefiroIncomingMessage) {
        if (!msg || !('command' in msg)) {
            return;
        }
        switch (msg.command) {
            case 'READY':
                return this.store.dispatch(seViewerConnected({ isConnected: true }));
            case 'ADD_KEY_IMAGE':
                return this.onAddKeyImage(msg.payload);
            case 'DELETE_KEY_IMAGE':
                return this.onDeleteKeyImage(msg.payload);
            case 'REFRESH_KEY_IMAGE':
                return this.onRefreshKeyImage(msg.payload);
            case 'KEY_IMAGES_INFO':
                return this.onSetKeyImages(msg.payload);
            case 'US_MEASURE_IMPORT_TO_REPORT':
                return this.onUsMeasureImport(msg.payload);
            case 'US_MEASURE_DELETE_SOURCE_AND_IMPORT_TO_REPORT':
                return this.onUsMeasureDelete(msg.payload.payload, msg.payload.replyCommand, msg.payload.requestId);
            case 'US_MEASURE_PMI_IMPORT_TO_REPORT':
                return this.onPMIMeasureImport(msg.payload);
            case 'US_STRESS_ECHO_INIT':
                return this.onUsStressEchoInit(msg.payload);
            case 'US_STRESS_ECHO_SELECT':
                return this.onUsStressEchoSelect(msg.payload);
            case 'ADD_GROUP_MEASURE':
                return this.onUnimplemented('addGroupMeasure');
            case 'DELETE_MEASURE':
                return this.onUnimplemented('deleteMeasure');
            case 'MODIFY_MEASURE':
                return this.onUnimplemented('modifyMeasure');
            case 'ADD_STRUCTURED_REPORT':
                return this.onSrAdd(msg.payload);
            case 'DELETE_STRUCTURED_REPORT':
                return this.onSrDelete(msg.payload);
            case 'XML_CAAS_DATA':
            case 'XML_CAAS_MR_DATA':
                return this.onXmlImport(XmlRequestType.Caas, msg.payload.xmlMessage);
            case 'XML_TERA_RECON_DATA':
                return this.onXmlImport(XmlRequestType.TeraRecon, msg.payload);
        }
    }

    private async buildViewerUrl(studyData?: PacsStudyDataVnext[]) {
        const userConfiguration = await firstValueFrom(this.userConfiguration.userConfigurationAsync);
        const pairingToken = await firstValueFrom(
            this.http.post<any>(`${this.getServerRoot()}/Authentication/GetUserPairingTokenWithData`, {
                licenseInfoList: JSON.stringify(userConfiguration.licenseInfoList),
            }),
        );

        let url = `${this.baseUrl}/internal/${pairingToken.token}`;

        if (studyData && !isEmpty(studyData)) {
            const payload = await this.encryptPayload(JSON.stringify(viewImagesModelFromPacsStudyData(studyData, {})));
            url += `/payload/${payload}`;
        }

        return url;
    }

    private async encryptPayload(payload: string) {
        if (!this.cryptoKey) {
            const jwk = await firstValueFrom(this.http.get(this.keysUrl, { headers: { 'X-Allow-Anonymous': 'true' } }));
            this.cryptoKey = await window.crypto.subtle.importKey(
                'jwk',
                jwk,
                {
                    name: 'RSA-OAEP',
                    hash: { name: 'SHA-256' },
                },
                false,
                ['encrypt'],
            );
        }

        const encoder = new TextEncoder();
        const encodedPayload = encoder.encode(payload);
        const chunks = [];
        const chunkSize = 128;
        for (let i = 0; i < encodedPayload.length; i += chunkSize) {
            const encryptedBuffer = await crypto.subtle.encrypt(
                { name: 'RSA-OAEP' },
                this.cryptoKey,
                encodedPayload.slice(i, i + chunkSize),
            );
            const b64 = btoa(String.fromCodePoint(...new Uint8Array(encryptedBuffer)));
            chunks.push(b64);
        }

        return chunks.join(':').replace(/\+/g, '-').replace(/\//g, '_');
    }

    //#region Viewer callbacks

    private listenForActions() {
        this.actions$.pipe(ofType(seViewerUSMeasureDeleteResponse)).subscribe((action) => {
            if (isViewerOpen(this.viewer)) {
                this.viewer.window.send({
                    command: action.callback,
                    channel: action.requestId,
                    payload: action.result,
                });
            }
        });
        this.actions$.pipe(ofType(seViewerUSMeasureGetReportResponse)).subscribe((action) => {
            if (isViewerOpen(this.viewer)) {
                this.viewer.window.send({
                    command: action.callback,
                    channel: action.requestId,
                    payload: action.report,
                });
            }
        });

        this.pingInterval = setInterval(() => {
            if (!isViewerOpen(this.viewer)) {
                return;
            }
            if (this.viewer.window.window.closed) {
                clearInterval(this.pingInterval);
                this.setViewerStateDisconnected();
            }
        }, 5000);
    }

    private onAddKeyImage(msg: ModelKeyImage) {
        this.store.dispatch(
            seViewerKeyImageAdd({
                image: keyImageFromMessage(msg),
                pacsTuple: msg.nPla,
            }),
        );
    }

    private onDeleteKeyImage(msg: ModelKeyImage) {
        this.store.dispatch(
            seViewerKeyImagesDelete({
                images: [
                    {
                        image: keyImageFromMessage(msg),
                        pacsTuple: msg.nPla,
                    },
                ],
            }),
        );
    }

    private onRefreshKeyImage(msg: ModelKeyImage) {
        this.store.dispatch(
            seViewerKeyImageRefresh({
                image: keyImageFromMessage(msg),
                pacsTuple: msg.nPla,
            }),
        );
    }

    private onSetKeyImages(msg: ModelKeyImageInstanceInfo[]) {
        this.store.dispatch(
            seViewerKeyImagesSet({
                images: msg.map((m) => ({
                    image: keyImageFromMessage(m),
                    pacsTuple: m.nPla,
                })),
            }),
        );
    }

    private onUsMeasureImport(msg: ModelUSMeasure) {
        this.store.dispatch(
            seViewerUSMeasureImportXmlRequest({
                nPla: convertPacsTupleDatesFromISO(msg.nPla),
                examGuid: msg.repositoryId,
            }),
        );
    }

    private onUsMeasureDelete(measure: ModelUSMeasure, callback: string, requestId: string) {
        this.store.dispatch(
            seViewerUSMeasureDeleteRequest({
                measure: convertPropertiesToCamelCase(measure),
                callback,
                requestId,
            }),
        );
    }

    async usMeasureShow(model: ModelUSMeasureShow): Promise<void> {
        if (!isViewerOpen(this.viewer)) {
            return;
        }

        return postMessageToWebViewer(this.viewer.window, {
            command: 'US_MEASURE_SHOW',
            payload: model.json,
        });
    }

    private onPMIMeasureImport(msg: ModelUSMeasure) {
        this.store.dispatch(
            seViewerUSMeasureImportJsonRequest({
                nPla: convertPacsTupleDatesFromISO(msg.nPla),
                repositoryName: msg.repositoryId,
            }),
        );
    }

    private onUsStressEchoInit(msg: ModelUSStressEchoInit) {
        this.store.dispatch(seViewerUSStressEchoInit(msg));
    }

    private onUsStressEchoSelect(msg: ModelUSStressEchoSelect) {
        this.store.dispatch(seViewerUSStressEchoSelect(msg));
    }

    private onSrDelete(msg: ModelStructuredReport) {
        this.store.dispatch(seViewerSRDelete(convertPropertiesToCamelCase(msg)));
    }

    private onSrAdd(msg: ModelStructuredReport) {
        this.store.dispatch(seViewerSRAdd(convertPropertiesToCamelCase(msg)));
    }

    private onXmlImport(requestType: XmlRequestType, msg: string) {
        switch (requestType) {
            case XmlRequestType.DiaImagingLVStrain:
            case XmlRequestType.DiaImagingRVStrain:
            case XmlRequestType.DiaImagingLVWallMotion:
                return this.store.dispatch(seViewerUSKineticMeasureImportRequest({ requestType, xml: msg }));
            case XmlRequestType.Caas:
            default:
                this.store.dispatch(seViewerUSMeasureImportRequestThirdParts({ requestType, xml: msg }));
        }
    }

    private onUnimplemented(messageName: string) {
        return () => {
            this.logger.debug(`Unimplemented viewer message: ${messageName}`);
        };
    }

    //#endregion
}
